import { gql } from '@apollo/client';

/**
 * Query to fetch attributes with their values filtered by shop_id
 * Only returns attributes that are actually used by products in the shop
 */
export const GET_ATTRIBUTES = gql`
  query GetAttributes {
    mst_attributes {
      id
      name
      attribute_values {
        id
        value
      }
    }
  }
`;

/**
 * Query to fetch categories with subcategories
 */
export const GET_CATEGORIES = gql`
  query GetCategories($shopId: uuid!) {
    mst_category(
      where: {
        isdeleted: { _eq: false }
        _or: [{ shop_id: { _eq: $shopId } }, { shop_id: { _is_null: true } }]
      }
    ) {
      id
      category
      image
    }
  }
`;

/** Fetch every subcategory so the storefront filters include entries
 * created in Super Admin even before a product is assigned to them. */
export const GET_SUBCATEGORIES = gql`
  query GetSubCategories {
    mst_sub_category {
      id
      sub_name
      category
      image
      gst_rate
      updated_at
      hsn_id
      hsn {
        id
        hsn_code
        gst_rate
      }
    }
  }
`;

/**
 * Query to fetch products with filters
 * Uses mst_product table from Hasura
 */
export const GET_PRODUCTS = gql`
  query GetProducts($limit: Int, $offset: Int, $shopId: uuid!) {
    mst_product_aggregate(
      where: {
        shop_id: { _eq: $shopId }
        isdeleted: { _eq: false }
        visible: { _eq: true }
      }
    ) {
      aggregate {
        count
      }
    }
    mst_product(
      limit: $limit
      offset: $offset
      where: {
        shop_id: { _eq: $shopId }
        isdeleted: { _eq: false }
        visible: { _eq: true }
      }
      order_by: { created_at: desc }
    ) {
      id
      name
      description
      selling_price
      offered_price
      mrp_price
      base_price
      images
      stocks
      quantity
      category
      sub_category
      shop_id
      created_at
      product_code
      product_no
      unit
      status
      admin_approval
      mst_category {
        id
        category
      }
      mst_sub_category {
        id
        sub_name
        gst_rate
        hsn_id
        hsn {
          id
          hsn_code
          gst_rate
        }
      }
      attributes
      mst_product_variants(
        where: { isdeleted: { _eq: false } }
        order_by: { created_at: asc }
      ) {
        id
        product_id
        variant_key
        attributes
        price
        offered_price
        images
        isdeleted
      }
    }
  }
`;

/**
 * Query to fetch a single product by ID
 * Uses mst_product table from Hasura
 */
export const GET_PRODUCT_BY_ID = gql`
  query GetProductById($id: uuid!, $shopId: uuid!) {
    mst_product(
      where: {
        id: { _eq: $id }
        shop_id: { _eq: $shopId }
        isdeleted: { _eq: false }
        visible: { _eq: true }
      }
      limit: 1
    ) {
      id
      name
      description
      selling_price
      offered_price
      mrp_price
      base_price
      images
      stocks
      quantity
      category
      sub_category
      shop_id
      created_at
      product_code
      product_no
      unit
      status
      admin_approval
      bar_code
      is_gst_included
      is_returnable
      return_policy
      show_stock
      top_selling
      trending_products
      mst_category {
        id
        category
      }
      mst_sub_category {
        id
        sub_name
        gst_rate
        hsn_id
        hsn {
          id
          hsn_code
          gst_rate
        }
      }
      attributes
      mst_product_variants(
        where: { isdeleted: { _eq: false } }
        order_by: { created_at: asc }
      ) {
        id
        product_id
        variant_key
        attributes
        price
        offered_price
        images
        isdeleted
      }
    }
  }
`;

/**
 * Batch-fetch products for reels (single round-trip instead of N GetProductById calls).
 */
export const GET_PRODUCTS_BY_IDS = gql`
  query GetProductsByIds($shopId: uuid!, $ids: [uuid!]!) {
    mst_product(
      where: {
        shop_id: { _eq: $shopId }
        isdeleted: { _eq: false }
        visible: { _eq: true }
        id: { _in: $ids }
      }
    ) {
      id
      name
      description
      selling_price
      offered_price
      mrp_price
      base_price
      images
      stocks
      quantity
      category
      sub_category
      shop_id
      created_at
      product_code
      product_no
      unit
      status
      admin_approval
      bar_code
      is_gst_included
      is_returnable
      return_policy
      show_stock
      top_selling
      trending_products
      mst_category {
        id
        category
      }
      mst_sub_category {
        id
        sub_name
        gst_rate
        hsn_id
        hsn {
          id
          hsn_code
          gst_rate
        }
      }
      attributes
      mst_product_variants(
        where: { isdeleted: { _eq: false } }
        order_by: { created_at: asc }
      ) {
        id
        product_id
        variant_key
        attributes
        price
        offered_price
        images
        isdeleted
      }
    }
  }
`;

/**
 * Query to fetch products for a specific category
 */
export const GET_PRODUCTS_BY_CATEGORY = gql`
  query GetProductsByCategory(
    $shopId: uuid!
    $categoryId: uuid!
    $limit: Int = 8
  ) {
    mst_product(
      where: {
        shop_id: { _eq: $shopId }
        category: { _eq: $categoryId }
        isdeleted: { _eq: false }
        visible: { _eq: true }
      }
      limit: $limit
      order_by: { created_at: desc }
    ) {
      id
      name
      description
      selling_price
      offered_price
      mrp_price
      images
      stocks
      quantity
      category
      sub_category
      product_code
      status
      visible
      admin_approval
      mst_category {
        id
        category
      }
      mst_product_variants(
        where: { isdeleted: { _eq: false } }
        order_by: { created_at: asc }
      ) {
        id
        product_id
        variant_key
        attributes
        price
        offered_price
        images
        isdeleted
      }
    }
  }
`;
